import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { P, isMatching, match } from 'ts-pattern'
import { z } from 'zod'

import { campaignQuery } from '@/cms/campaign'
import { runQuery } from '@/cms/client'
import { abstractProductQuery } from '@/cms/contentBlock'
import { routeQuery } from '@/cms/pages'
import type { WebUrl } from '@/cms/webUrl'
import { blogPostSlugQuery, buyingFlowQuery, pdfQuery, websiteQuery } from '@/cms/webUrl'
import { reportErrorMessage } from '@/services/errorReporting'
import type { StartFlowSessionParams } from '@/services/flowSession'
import { getBuyingFlowSessionUrl, startFlowSession } from '@/services/flowSession'
import { getLocaleParts } from '@/utils/locale'
import {
  getAppendedFullUrl,
  getBuyingFlowUrl,
  getPathWithParams,
  removeImpermanentSearchParams,
} from '@/utils/url'
import { useQuery } from '@tanstack/react-query'

const extensionsLandingPagesPaths = [
  'family-liability',
  'contents-glass-de',
  'familien-haftpflicht',
  'hausrat-glas-de',
]

const getConfigParams = (flowName: string) => {
  if (flowName === 'liability') {
    return { family_coverage: true }
  } else if (flowName === 'contents') {
    return {
      glass_coverage: true,
    }
  }
}

const liabilityConfigParamsSchema = z.object({
  family_coverage: z.boolean(),
})

const contentsConfigParamsSchema = z.object({
  living_space_square_meters: z.number().optional(),
  glass_coverage: z.boolean(),
})

const configParamsSchema = liabilityConfigParamsSchema.or(contentsConfigParamsSchema)

const flowNameSchema = z.literal('liability').or(z.literal('contents'))

export const useUrlFromWebUrlReference = (
  webUrlArray?: WebUrl,
  options: { optional?: boolean } = {}
) => {
  const { locale, query: urlPathParams, asPath } = useRouter()
  const [lang, market] = getLocaleParts(locale)
  const isExtensionsPage = extensionsLandingPagesPaths.some((path) => asPath.includes(path))
  const whitelistedParams = useMemo(
    () => removeImpermanentSearchParams(urlPathParams).toString(),
    [urlPathParams]
  )
  if (!options.optional && (!webUrlArray || webUrlArray.length === 0)) {
    throw new Error('Assertion: webUrlArray cannot be undefined')
  }
  const { data: href } = useQuery<string>({
    queryKey: ['webUrl', locale, JSON.stringify(webUrlArray?.[0])],
    queryFn: async () => {
      if (!webUrlArray || webUrlArray.length === 0) {
        throw new Error('Assertion: webUrlArray cannot be undefined')
      }
      const webUrl = webUrlArray[0]
      switch (webUrl._type) {
        case 'blogPost_2': {
          const blogPost = await runQuery(blogPostSlugQuery, { lang, id: webUrl.post._ref })
          if (blogPost._type !== 'blogPost') {
            throw new Error('Assertion: blogPost doc is of wrong type')
          }
          const anchor = webUrl.anchor ? `#${webUrl.anchor}` : ''
          return getPathWithParams(`/posts/${blogPost.slug}`, { params: whitelistedParams, anchor })
        }

        case 'product': {
          const abstractProduct = await runQuery(abstractProductQuery, {
            lang,
            id: webUrl.abstractProduct._ref,
          })
          if (!abstractProduct) {
            return ''
          }
          const { folder, slug } = abstractProduct.website
          const url = [folder, slug.replace('index', '')].filter(Boolean).join('/')
          return getPathWithParams(`/${url}`, { params: whitelistedParams })
        }

        case 'internalWebsite_2': {
          const route = await runQuery(websiteQuery, { lang, id: webUrl.route._ref })
          if (!route) {
            return ''
          }
          if ('redirectActive' in route && route.redirectActive === true) {
            reportErrorMessage(`Rendered link to URL which is being redirected: "${route.slug}"`)
          }
          const { folder, slug } = route
          const url = [folder, slug.replace('index', '')].filter(Boolean).join('/')
          const anchor = webUrl.anchor ? `#${webUrl.anchor}` : ''

          return getPathWithParams(`/${url}`, { params: whitelistedParams, anchor })
        }

        case 'buyingFlow': {
          const [flow, campaign] = await Promise.all([
            runQuery(buyingFlowQuery.nullable(), { lang, id: webUrl.flow?._ref ?? null }),
            webUrl.campaign ? runQuery(campaignQuery, { lang, id: webUrl.campaign._ref }) : null,
          ])
          if (!flow) {
            return ''
          }
          if (flow._type !== 'website.buyingFlow') {
            throw new Error('Assertion: buyingFlow doc is of wrong type')
          }
          if (campaign && campaign._type !== 'campaign') {
            throw new Error('Assertion: campaign doc is of wrong type')
          }

          if (isExtensionsPage) {
            const flowNameResult = flowNameSchema.safeParse(flow?.slug)
            if (!flowNameResult.success) {
              return ''
            }
            const flowName = flowNameResult.data

            const sessionParams = {
              lang,
              market,
              flowName,
            }

            const configParamsResult = configParamsSchema.safeParse(getConfigParams(flowName))
            if (!configParamsResult.success) {
              return ''
            }

            const configParams = configParamsResult.data

            const response = await startFlowSession({
              ...sessionParams,
              configParams,
            } as StartFlowSessionParams)

            return getBuyingFlowSessionUrl({
              ...sessionParams,
              sessionId: response.data.id,
            })
          }

          const folderPath = urlPathParams?.slug?.[0]
          const slugPath = urlPathParams?.slug?.[1]

          const pageTypeQuery = async () =>
            // There is actually no problem with this type but Zod has sometimes problems
            // with very deep types
            // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
            // @ts-ignore-next-line
            await runQuery(routeQuery, {
              lang,
              market,
              slug: slugPath || folderPath || 'index',
              folder: folderPath,
            })

          const buyingFlowProductPage = await match(urlPathParams?.slug)
            .with(P.not(P.nullish), pageTypeQuery)
            .otherwise(() => null)

          const isMgaLightProductPage = isMatching(
            'website.pageType.mgaLightProductPage',
            buyingFlowProductPage?.pageType?._type
          )

          return getBuyingFlowUrl({
            flowName: flow.slug,
            locale: locale as string,
            campaign: campaign?.name,
            coupon: webUrl.coupon ?? undefined,
            extraParams: new URLSearchParams(whitelistedParams),
            useLandingPageHost: isMgaLightProductPage,
          })
        }
        case 'pdf': {
          const doc = await runQuery(pdfQuery, { id: webUrl._ref })
          if (doc._type === 'doc') {
            return `/documents/${doc.fileName}`
          }
          return `/documents/${doc.fileName}?lang=${lang}`
        }

        case 'externalWebsite':
          return getAppendedFullUrl(webUrl.url, whitelistedParams)

        default: {
          // @ts-expect-error
          reportErrorMessage(`webUrl with type "${webUrl._type}" not supported`)
          return ''
        }
      }
    },
    enabled: !options.optional || (webUrlArray?.length ?? 0) > 0,
    throwOnError: true,
  })

  return href ?? asPath
}
